import React, { useCallback, useContext, useEffect, useState } from 'react';
import dataProvider from '../../../../../core/providers/IccUserDataProvider';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import useNotifyServerError from '../../../../../core/hooks/useNotifyServerError';
import { Loading } from 'ra-ui-materialui';
import { makeStyles, MenuItem } from '@material-ui/core';
import ImportPermissionsContext from '../ImportPermissionsContext';
import { AutoSizer, Column, Table } from 'react-virtualized';
import clsx from 'clsx';
import InputLabel from '@material-ui/core/InputLabel';
import { FieldTitle } from 'react-admin';
import TextField from '@material-ui/core/TextField';
import SelectField from '@material-ui/core/Select';
import { USERTYPE } from '../../../EnumUserType';

const tableHeadCells = [
  { label: 'Contact Name', dataKey: 'custom', width: 1.5 * 1200 },
  { label: 'Email Address', dataKey: 'email', width: 1.2 * 500 },
  { label: 'User ID', dataKey: 'userId', width: 1.5 * 128 }
];
const useStyles = makeStyles({
  loading: {
    width: '100%',
    height: 'calc(100vh - 230px)'
  },
  contactContainer: {
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'flex-start'
  },
  contactContainerForCreation: {
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'flex-start',
    height: 'calc(100% - 42px)'
  },
  text: {
    fontSize: '14px',
    fontWeight: 400
  },
  flexContainer: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box'
  },
  gridContainer: {
    width: 'calc(100%)',
    height: 'calc(100% - 116px)'
  },
  noMatchContainer: {
    display: 'flex',
    justifyContent: 'center'
  },
  border: {
    borderTop: '1px solid #E0E0E0',
    paddingRight: '0px !important'
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'space-between'
  },
  searchBar: {
    width: '69%'
  },
  searchBarFullWidth: {
    width: '100%'
  },
  investorsFilterContainer: {
    width: '30%'
  },
  selectField: {
    width: '100%',
    margin: '8px 0px 8px 0px'
  },
  noBorderBottom: {
    borderBottom: '0px !important'
  },
  hoverRow: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    }
  }
});
const headerRenderer = ({ label, classes }) => {
  return (
    <TableCell
      variant="head"
      className={clsx(classes.flexContainer, classes.noBorderBottom)}
    >
      {label}
    </TableCell>
  );
};

const ContactStep = props => {
  const {
    creation,
    isRegular: isRegularCreation,
    userToCopy: userToCopyCreation,
    updateUser: updateUserCreation,
    userId: userIdCreation,
    setSelectedContactForImport,
    setIsNewImportToDo
  } = props;
  const notifyServerError = useNotifyServerError();
  const classes = useStyles();
  const [contacts, setContacts] = useState([]);
  const [investorSelected, setInvestorSelected] = useState('all_Investors');
  const [investors, setInvestors] = useState([
    { id: 'all_Investors', name: 'All Investors' }
  ]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [
    filteredContactsForFiltering,
    setFilteredContactsForFiltering
  ] = useState([]);
  const { isRegular, updateUser, userToCopy, userId } = useContext(
    ImportPermissionsContext
  );
  const [selectedRow, setSelectedRow] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchUsers = useCallback(
    (isUserRegular, IdOfUser) => {
      dataProvider('GET_LIST', 'users', {
        paginated: false,
        filter: {
          userType: isUserRegular
            ? USERTYPE.regularContact
            : USERTYPE.fundAdmin,

          orphanVisibility: 'NON_ORPHANS_ONLY',
          orderBy: 'fullName',
          orderDirection: 'ASC'
        }
      })
        .then(({ data }) => {
          if (data) {
            setContacts(data.filter(contact => contact.userId !== IdOfUser));
            setFilteredContacts(
              data.filter(contact => contact.userId !== IdOfUser)
            );
          }
        })
        .catch(error => notifyServerError(error, undefined))
        .finally(() => setLoaded(true));

      isUserRegular &&
        dataProvider('GET_LIST', 'investors', {
          paginated: false,
          filter: {
            orphanVisibility: 'NON_ORPHANS_ONLY',
            orderBy: 'name',
            orderDirection: 'ASC'
          }
        })
          .then(({ data }) => {
            if (data) {
              setInvestors(previousStateInvestors =>
                previousStateInvestors.concat(data)
              );
            }
          })
          .catch(notifyServerError)
          .finally(() => setLoaded(true));
    },
    [notifyServerError]
  );

  useEffect(() => {
    !creation
      ? fetchUsers(isRegular, userId)
      : fetchUsers(isRegularCreation, userIdCreation);
  }, [
    notifyServerError,
    isRegular,
    fetchUsers,
    userId,
    creation,
    isRegularCreation,
    userIdCreation
  ]);

  //return true if the selected user is in the array
  const isSelected = useCallback(
    user =>
      !creation
        ? user.userId === (userToCopy && userToCopy.userId)
        : user.userId === (userToCopyCreation && userToCopyCreation.userId),
    [userToCopy, userToCopyCreation, creation]
  );

  // Manage the rowClick, to choose one and only one record in the list
  const rowClick = useCallback(
    user => {
      const selectedUser = selectedRow?.userId === user.userId;
      let newSelected;
      if (selectedUser) {
        newSelected = null;
      } else {
        // add the usertype property if we are filtering the list from investor,
        // because the contacts from the investors/contacts api aren't populated with the usertype and it's mandatory for the second step
        // override the userType with the same value as before in case we are coming from a FA, since we don't filter by investor for an FA
        if (!creation) {
          newSelected = {
            ...user,
            userType: isRegular ? USERTYPE.regularContact : USERTYPE.fundAdmin
          };
        } else {
          newSelected = {
            ...user,
            userType: isRegularCreation
              ? USERTYPE.regularContact
              : USERTYPE.fundAdmin
          };
        }
      }

      if (!creation) {
        setSelectedRow(newSelected);
      } else {
        setIsNewImportToDo(true);
        setSelectedContactForImport(newSelected);
      }
      !creation ? updateUser(newSelected) : updateUserCreation(newSelected);
    },
    [
      selectedRow?.userId,
      creation,
      updateUser,
      updateUserCreation,
      isRegular,
      isRegularCreation,
      setIsNewImportToDo,
      setSelectedContactForImport
    ]
  );

  /**
   * Set the itemSearch state to filter the right side list
   */
  const handleItemSearch = useCallback(
    text => {
      const searchArray =
        investorSelected !== 'all_Investors'
          ? filteredContactsForFiltering
          : contacts;
      const searchText = text.toLowerCase();
      setFilteredContacts(() => {
        let newArray = [];
        searchArray.forEach(contact => {
          const fullName = `${contact.firstName} ${contact.lastName}`;
          if (
            fullName.toLowerCase().includes(searchText) ||
            contact.userId.toLowerCase().includes(searchText) ||
            contact.email.toLowerCase().includes(searchText)
          ) {
            newArray.push(contact);
          }
        });
        return newArray;
      });
    },
    [contacts, investorSelected, filteredContactsForFiltering]
  );

  const cellRenderer = useCallback(
    params => {
      const { dataKey, rowData, cellData } = params;
      const cellValue =
        dataKey === 'custom'
          ? `${rowData.firstName || ''} ${rowData.lastName || ''}`
          : cellData;
      const isItemSelected = !creation
        ? isSelected(rowData, userToCopy)
        : isSelected(rowData, userToCopyCreation);
      return (
        <TableRow selected={isItemSelected} style={{ width: '100%' }}>
          <TableCell component="div" variant="body" style={{ height: 30 }}>
            <div style={{ display: 'flex' }}>{cellValue}</div>
          </TableCell>
        </TableRow>
      );
    },
    [isSelected, creation, userToCopy, userToCopyCreation]
  );
  const handleOnChangeInvestorsFilter = useCallback(
    value => {
      setInvestorSelected(value);
      if (value === 'all_Investors') {
        setFilteredContacts(contacts);
      } else {
        dataProvider('GET_LIST', 'investorcontacts', {
          paginated: false,
          filter: {
            investorId: value,
            orderBy: 'firstName',
            orderDirection: 'ASC'
          }
        })
          .then(({ data }) => {
            if (data) {
              setFilteredContacts(data);
              setLoaded(true);
              setFilteredContactsForFiltering(data);
            }
          })
          .catch(notifyServerError);
      }
    },
    [contacts, notifyServerError]
  );
  if (!loaded) return <Loading className={classes.loading} />;
  return (
    <div
      className={
        !creation
          ? classes.contactContainer
          : classes.contactContainerForCreation
      }
    >
      {!creation && (
        <Typography
          variant="h5"
          style={{ paddingTop: '0px', paddingBottom: '10px' }}
        >
          Select Contact to copy it's permission groups
        </Typography>
      )}
      <div className={classes.searchContainer}>
        <div
          className={
            !(isRegular || isRegularCreation)
              ? classes.searchBarFullWidth
              : classes.searchBar
          }
        >
          <InputLabel htmlFor="searchBar">
            <FieldTitle
              label={
                !creation
                  ? 'Search by Contact Name, Email, or User ID'
                  : 'Select a Contact to import its permission groups.'
              }
              source="searchBar"
              resource="users"
            />
          </InputLabel>
          <TextField
            margin="dense"
            placeholder={`Search...`}
            onChange={e => handleItemSearch(e.target.value)}
          />
        </div>
        {(isRegular || isRegularCreation) && (
          <div className={classes.investorsFilterContainer}>
            <InputLabel htmlFor="investors_filter">
              <FieldTitle
                label="Show Contacts From:"
                source="investors_filter"
                resource="investors"
              />
            </InputLabel>
            <SelectField
              displayEmpty
              margin="dense"
              className={classes.selectField}
              onChange={e => handleOnChangeInvestorsFilter(e.target.value)}
              value={investorSelected}
            >
              {investors.map(({ id, name }) => (
                <MenuItem value={id} key={id}>
                  {name}
                </MenuItem>
              ))}
            </SelectField>
          </div>
        )}
      </div>

      <div className={classes.gridContainer}>
        {filteredContacts.length === 0 && (
          <div className={classes.noMatchContainer}>
            <Typography variant="h5">No results found</Typography>
          </div>
        )}
        {filteredContacts.length > 0 && (
          <AutoSizer>
            {({ height, width }) => (
              <Table
                width={width}
                height={height}
                columnCount={tableHeadCells.length}
                columnWidth={100}
                rowCount={filteredContacts.length}
                rowGetter={({ index }) => filteredContacts[index]}
                headerHeight={30}
                gridStyle={{ border: '1px solid #E0E0E0', borderTop: '0' }}
                rowHeight={30}
                rowClassName={clsx(
                  classes.flexContainer,
                  classes.border,
                  classes.hoverRow
                )}
                estimatedRowSize={30}
                overscanRowCount={10}
                onRowClick={({ rowData }) => rowClick(rowData)}
              >
                {tableHeadCells.map(({ dataKey, ...other }) => {
                  return (
                    <Column
                      key={dataKey}
                      headerRenderer={headerProps =>
                        headerRenderer({
                          ...headerProps,
                          classes
                        })
                      }
                      cellRenderer={cellRenderer}
                      className={classes.flexContainer}
                      dataKey={dataKey}
                      {...other}
                    />
                  );
                })}
              </Table>
            )}
          </AutoSizer>
        )}
      </div>
    </div>
  );
};

export default ContactStep;
